import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { usesWeChat } from '@/lib/agencyContact';
import { fixedRegionsForCountry, regionCode, regionDisplay } from '@/lib/addressRegions';
import { agencyProgramOptionLabel } from '@/lib/agencyDashboard';
import { customFieldKey } from '@/lib/agencyFields';
import { Plus } from 'lucide-react';

export const UNASSIGNED_OWNER = '__unassigned__';
export const NO_AGENCY_TYPE = '__no_agency_type__';

const PROVINCE_STATE_COUNTRIES = new Set(['CA', 'US']);

const NATURE_OF_BUSINESS_OPTIONS = [
  'Immigration Consultancy',
  'Student Service Provider',
  'Training School',
  'Other',
];

const EMPLOYEE_COUNT_OPTIONS = ['0-3', '4-10', '10+'];

const EDUCATION_LEVEL_OPTIONS = [
  'High School',
  'College / Diploma',
  "Bachelor's Degree",
  'Postgraduate / Graduate',
  'Language Training',
  'Professional / Career Training',
];

const COMMUNICATION_CHANNEL_OPTIONS = [
  'Email',
  'Phone',
  'WhatsApp',
  'WeChat',
  'Line',
  'Social Media',
  'Video Call',
  'Other',
];

const PROMOTIONAL_CHANNEL_OPTIONS = [
  'Paid Online Advertisement',
  'Education/Career Fair',
  'Referral',
  'Social Media Marketing',
  'Newspaper/Magazine',
  'Radio/TV',
  'Social Events',
];

const HEAR_ABOUT_OPTIONS = [
  'MCC Website',
  'MCC Representative',
  'Referral',
  'Education/Career Fair',
  'Social Media',
  'Search Engine',
  'Other',
];

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function labelize(value) {
  return String(value || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Not set';
}

function normalizeCountryCode(value) {
  const text = String(value || '').trim();
  const legacy = {
    Canada: 'CA',
    canada: 'CA',
    China: 'CN',
    china: 'CN',
    India: 'IN',
    india: 'IN',
    Philippines: 'PH',
    philippines: 'PH',
    Taiwan: 'TW',
    taiwan: 'TW',
    Vietnam: 'VN',
    vietnam: 'VN',
    'Hong Kong': 'HK',
    'United States': 'US',
  };
  return legacy[text] || text.toUpperCase();
}

function countryOptions(meta) {
  return safeList(meta?.countries).filter((country) => country?.code);
}

function addressProvinceOptions(meta, countryCode) {
  const normalized = normalizeCountryCode(countryCode);
  if (!PROVINCE_STATE_COUNTRIES.has(normalized)) return [];
  return fixedRegionsForCountry(normalized);
}

function tierLabel(tier, tierLabels) {
  const normalized = String(Number(tier ?? 0));
  return tierLabels?.[normalized] || tierLabels?.[Number(normalized)] || `Tier ${normalized}`;
}

function programOptionsWithLegacy(programs, selectedPrograms) {
  const canonical = safeList(programs).filter(Boolean);
  const seen = new Set(canonical);
  const legacy = safeList(selectedPrograms).filter((program) => program && !seen.has(program));
  return [...canonical, ...legacy];
}

function listWithoutIndex(values, index) {
  return safeList(values).filter((_, itemIndex) => itemIndex !== index);
}

function hasSecondaryContact(form) {
  return Object.values(form?.secondaryContact || {}).some((value) => String(value || '').trim());
}

function Field({ label, children, className }) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-4 border-t border-[rgba(6,43,26,0.08)] pt-4 first:border-t-0 first:pt-0">
      <h3 className="text-sm font-semibold text-[#0B6B4B]">{title}</h3>
      {children}
    </section>
  );
}

function Subheading({ children }) {
  return (
    <div className="sm:col-span-2">
      <p className="text-sm font-semibold text-[#062B1A]">{children}</p>
    </div>
  );
}

// Renders one admin-defined field using the control its declared type implies.
function CustomFieldInput({ definition, value, onChange, inputClass }) {
  if (definition.type === 'select') {
    return (
      <Select value={String(value || '')} onValueChange={onChange}>
        <SelectTrigger className={inputClass}>
          <SelectValue placeholder="Not set" />
        </SelectTrigger>
        <SelectContent>
          {safeList(definition.options).map((option) => (
            <SelectItem key={option} value={option}>{option}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (definition.type === 'boolean') {
    return (
      <Select
        value={value === true ? 'yes' : value === false ? 'no' : ''}
        onValueChange={(next) => onChange(next === 'yes')}
      >
        <SelectTrigger className={inputClass}>
          <SelectValue placeholder="Not set" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="yes">Yes</SelectItem>
          <SelectItem value="no">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type={definition.type === 'number' ? 'number' : definition.type === 'date' ? 'date' : 'text'}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value)}
      className={inputClass}
    />
  );
}

function CheckboxCards({ options, value, onChange }) {
  const selected = safeList(value);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {options.map((option) => (
        <label
          key={option}
          className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-[rgba(6,43,26,0.12)] bg-white px-3 py-2 text-sm font-medium text-[#062B1A]"
        >
          <Checkbox
            checked={option === 'WeChat' ? usesWeChat(selected) : selected.includes(option)}
            onCheckedChange={(checked) => {
              if (checked) {
                if (option === 'WeChat' && usesWeChat(selected)) return;
                onChange([...selected, option]);
                return;
              }
              onChange(selected.filter((item) => (
                option === 'WeChat' ? !usesWeChat([item]) : item !== option
              )));
            }}
          />
          <span>{option}</span>
        </label>
      ))}
    </div>
  );
}

function RankedSelects({
  labels,
  values,
  options,
  placeholder,
  onChange,
  optionLabel = (option) => option,
}) {
  const selected = safeList(values);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {labels.map((label, index) => {
        const current = selected[index] || '';
        const available = options.filter((option) => option === current || !selected.includes(option));
        return (
          <Field key={label} label={label}>
            <Select value={current || undefined} onValueChange={(value) => onChange(index, value)}>
              <SelectTrigger title={current || undefined}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {available.map((option) => (
                  <SelectItem key={option} value={option} title={option}>
                    {optionLabel(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        );
      })}
    </div>
  );
}

export default function AgencyProfileForm({ form, setForm, meta, inputClass, sinPlaceholder, customFields = [] }) {
  const customValues = form.customFields && typeof form.customFields === 'object' ? form.customFields : {};
  const setCustomField = (definition, value) => setForm((prev) => ({
    ...prev,
    customFields: {
      ...(prev.customFields || {}),
      [customFieldKey(definition.id)]: value === '' ? null : value,
    },
  }));
  const countries = countryOptions(meta);
  const selectedProvinces = addressProvinceOptions(meta, form.country);
  const statusOptions = safeList(meta.statuses).map((status) => ({ value: status, label: labelize(status) }));
  const programOptions = programOptionsWithLegacy(meta.programs, form.programsOfInterest);

  const setText = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const setField = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setContactText = (contactKey, key) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [contactKey]: { ...prev[contactKey], [key]: value },
    }));
  };
  const setArrayField = (key) => (values) => setForm((prev) => ({ ...prev, [key]: values }));
  const setRankedField = (key) => (index, value) => {
    setForm((prev) => {
      const next = [...safeList(prev[key])];
      next[index] = value;
      return { ...prev, [key]: next.slice(0, 3) };
    });
  };
  const setCountry = (value) => {
    setForm((prev) => ({ ...prev, country: value, province: '' }));
  };
  const setAgencyType = (value) => {
    const agencyType = value === NO_AGENCY_TYPE ? '' : value;
    setForm((prev) => ({
      ...prev,
      agencyType,
      companyRegistrationNumber: agencyType === 'Domestic' || agencyType === 'International' ? prev.companyRegistrationNumber : '',
    }));
  };
  const showSecondaryContact = () => {
    setForm((prev) => ({ ...prev, showSecondaryContact: true }));
  };
  const addCommissionRow = () => {
    setForm((prev) => ({
      ...prev,
      commissionSchedule: [...safeList(prev.commissionSchedule), { year: '', percent: '' }],
    }));
  };
  const removeCommissionRow = (index) => {
    setForm((prev) => {
      const next = listWithoutIndex(prev.commissionSchedule, index);
      return { ...prev, commissionSchedule: next.length ? next : [{ year: '', percent: '' }] };
    });
  };
  const setCommissionRow = (index, key) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      commissionSchedule: safeList(prev.commissionSchedule).map((row, rowIndex) => (
        rowIndex === index ? { ...row, [key]: value } : row
      )),
    }));
  };

  const secondaryVisible = form.showSecondaryContact || hasSecondaryContact(form);
  const registrationNumberRequired = form.agencyType === 'Domestic' || form.agencyType === 'International';

  return (
    <div className="space-y-6">
      <Section title="1. Agency Contact Information">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Company legal name">
            <Input value={form.company_name} onChange={setText('company_name')} className={inputClass} />
          </Field>
          <Field label="Company preferred/brand name">
            <Input value={form.preferredName} onChange={setText('preferredName')} className={inputClass} />
          </Field>
          <Field label="Company phone number">
            <Input value={form.phone} onChange={setText('phone')} className={inputClass} />
          </Field>
          <Field label="Company email address">
            <Input value={form.email} onChange={setText('email')} className={inputClass} />
          </Field>
          <Field label="Company website" className="sm:col-span-2">
            <Input value={form.website} onChange={setText('website')} className={inputClass} placeholder="https://" />
          </Field>

          <Subheading>Company Address</Subheading>
          <Field label="Apt/Unit/Suite #">
            <Input value={form.unitNumber || ''} onChange={setText('unitNumber')} className={inputClass} />
          </Field>
          <Field label="Street Address">
            <Input value={form.address} onChange={setText('address')} className={inputClass} />
          </Field>
          <Field label="City">
            <Input value={form.city} onChange={setText('city')} className={inputClass} />
          </Field>
          <Field label="Country">
            <Select value={form.country || undefined} onValueChange={setCountry}>
              <SelectTrigger className={inputClass}><SelectValue placeholder="Select Country" /></SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.code} value={country.code}>{country.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Province/State">
            {selectedProvinces.length ? (
              <Select value={regionDisplay(form.country, form.province) || undefined} onValueChange={setField('province')}>
                <SelectTrigger className={inputClass}><SelectValue placeholder="Select province/state" /></SelectTrigger>
                <SelectContent>
                  {selectedProvinces.map((province) => (
                    <SelectItem key={province.code} value={province.code}>{province.code} — {province.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input value={form.province} onChange={setText('province')} className={inputClass} />
            )}
          </Field>
          <Field label="Postal Code">
            <Input value={form.postalCode} onChange={setText('postalCode')} className={inputClass} />
          </Field>

          <Subheading>Main Contact Person</Subheading>
          <Field label="Full name">
            <Input value={form.mainContact.name} onChange={setContactText('mainContact', 'name')} className={inputClass} />
          </Field>
          <Field label="Preferred name">
            <Input value={form.mainContact.preferredName} onChange={setContactText('mainContact', 'preferredName')} className={inputClass} />
          </Field>
          <Field label="Job title">
            <Input value={form.mainContact.title} onChange={setContactText('mainContact', 'title')} className={inputClass} />
          </Field>
          <Field label="Phone number">
            <Input value={form.mainContact.phone} onChange={setContactText('mainContact', 'phone')} className={inputClass} />
          </Field>
          <Field label="Email address">
            <Input value={form.mainContact.email} onChange={setContactText('mainContact', 'email')} className={inputClass} />
          </Field>

          {!secondaryVisible && (
            <div className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={showSecondaryContact}
                className="border-[#D4AF37] text-[#8A6A00] hover:bg-[#D4AF37]/10"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Secondary Contact Person
              </Button>
            </div>
          )}

          {secondaryVisible && (
            <>
              <Subheading>Secondary Contact Person</Subheading>
              <Field label="Full name">
                <Input value={form.secondaryContact.name} onChange={setContactText('secondaryContact', 'name')} className={inputClass} />
              </Field>
              <Field label="Preferred name">
                <Input value={form.secondaryContact.preferredName} onChange={setContactText('secondaryContact', 'preferredName')} className={inputClass} />
              </Field>
              <Field label="Job title">
                <Input value={form.secondaryContact.title} onChange={setContactText('secondaryContact', 'title')} className={inputClass} />
              </Field>
              <Field label="Phone number">
                <Input value={form.secondaryContact.phone} onChange={setContactText('secondaryContact', 'phone')} className={inputClass} />
              </Field>
              <Field label="Email address">
                <Input value={form.secondaryContact.email} onChange={setContactText('secondaryContact', 'email')} className={inputClass} />
              </Field>
            </>
          )}

          <div className="sm:col-span-2">
            <Label>Preferred Channel of Communication</Label>
            <div className="mt-2">
              <CheckboxCards
                options={COMMUNICATION_CHANNEL_OPTIONS}
                value={form.communicationChannels}
                onChange={setArrayField('communicationChannels')}
              />
            </div>
          </div>
          {usesWeChat(form.communicationChannels) && (
            <Field label="Main contact WeChat ID" className="sm:col-span-2">
              <Input
                value={form.mainContact.wechatId || ''}
                onChange={setContactText('mainContact', 'wechatId')}
                className={inputClass}
                autoComplete="off"
                maxLength={100}
                placeholder="Enter the contact's WeChat ID (optional)"
              />
              <p className="text-xs leading-relaxed text-[#062B1A]/55">
                WeChat ID is optional. Company and main-contact phone/email are also optional while WeChat is selected.
              </p>
            </Field>
          )}
          {safeList(form.communicationChannels).includes('Other') && (
            <Field label="Other communication channel" className="sm:col-span-2">
              <Input value={form.communicationChannelOther} onChange={setText('communicationChannelOther')} className={inputClass} />
            </Field>
          )}
        </div>
      </Section>

      <Section title="2. Agency Business Information">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Nature of business">
            <Select value={form.natureOfBusiness || undefined} onValueChange={setField('natureOfBusiness')}>
              <SelectTrigger className={inputClass}><SelectValue placeholder="Choose an option" /></SelectTrigger>
              <SelectContent>
                {NATURE_OF_BUSINESS_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Number of Employees">
            <Select value={form.numberOfEmployees || undefined} onValueChange={setField('numberOfEmployees')}>
              <SelectTrigger className={inputClass}><SelectValue placeholder="Choose an option" /></SelectTrigger>
              <SelectContent>
                {EMPLOYEE_COUNT_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {form.natureOfBusiness === 'Other' && (
            <Field label="Other nature of business" className="sm:col-span-2">
              <Input value={form.natureOfBusinessOther} onChange={setText('natureOfBusinessOther')} className={inputClass} />
            </Field>
          )}
          <Field label="GST Number (if applicable)">
            <Input value={form.gstNumber} onChange={setText('gstNumber')} className={inputClass} />
          </Field>
          <Field label="SIN Number (if applicable)">
            <Input value={form.sin} onChange={setText('sin')} className={inputClass} placeholder={sinPlaceholder || ''} />
          </Field>
          <Field label="Number of students sent to Canada in the past 12 months">
            <Input type="number" min="0" value={form.studentsSentToCanadaPast12Months} onChange={setText('studentsSentToCanadaPast12Months')} className={inputClass} />
          </Field>
          <Field label="Number of students anticipated for MCC in the next 12 months">
            <Input type="number" min="0" value={form.studentsAnticipatedForMccNext12Months} onChange={setText('studentsAnticipatedForMccNext12Months')} className={inputClass} />
          </Field>

          <div className="sm:col-span-2">
            <Label>Top 3 countries your students come from</Label>
            <div className="mt-2">
              <RankedSelects
                labels={['Country 1', 'Country 2', 'Country 3']}
                values={form.studentSourceCountries}
                options={countries.map((country) => country.name)}
                placeholder="Select Country"
                onChange={setRankedField('studentSourceCountries')}
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <Label>Top 3 target education levels your students seek</Label>
            <div className="mt-2">
              <RankedSelects
                labels={['Level 1', 'Level 2', 'Level 3']}
                values={form.targetEducationLevels}
                options={EDUCATION_LEVEL_OPTIONS}
                placeholder="Choose an option"
                onChange={setRankedField('targetEducationLevels')}
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <Label>Top programs of interest</Label>
            <div className="mt-2">
              <RankedSelects
                labels={['Program 1', 'Program 2', 'Program 3']}
                values={form.programsOfInterest}
                options={programOptions}
                placeholder="Choose a program"
                onChange={setRankedField('programsOfInterest')}
                optionLabel={(program) => agencyProgramOptionLabel(program, meta)}
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <Label>Promotional channel(s) used in the past 12 months</Label>
            <div className="mt-2">
              <CheckboxCards
                options={PROMOTIONAL_CHANNEL_OPTIONS}
                value={form.promotionalChannels}
                onChange={setArrayField('promotionalChannels')}
              />
            </div>
          </div>

          <Field label="How did you hear about MCC?">
            <Select value={form.howDidYouHearAboutMcc || undefined} onValueChange={setField('howDidYouHearAboutMcc')}>
              <SelectTrigger className={inputClass}><SelectValue placeholder="Choose an option" /></SelectTrigger>
              <SelectContent>
                {HEAR_ABOUT_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {form.howDidYouHearAboutMcc === 'Other' && (
            <Field label="Other source">
              <Input value={form.howDidYouHearAboutMccOther} onChange={setText('howDidYouHearAboutMccOther')} className={inputClass} />
            </Field>
          )}
        </div>
      </Section>

      <Section title="3. Portal Management">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Type of agency">
            <Select value={form.agencyType || NO_AGENCY_TYPE} onValueChange={setAgencyType}>
              <SelectTrigger className={inputClass}><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_AGENCY_TYPE}>Not set</SelectItem>
                {safeList(meta.agencyTypes).map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {registrationNumberRequired && (
            <Field label="Company registration number">
              <Input value={form.companyRegistrationNumber} onChange={setText('companyRegistrationNumber')} className={inputClass} />
            </Field>
          )}
          <Field label="Tier of Agency">
            <Select value={form.tier} onValueChange={setField('tier')}>
              <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
              <SelectContent>
                {safeList(meta.tiers).map((tier) => (
                  <SelectItem key={tier} value={String(tier)}>
                    Tier {tier} - {tierLabel(tier, meta.tierLabels)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={setField('status')}>
              <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="MCC Representative">
            <Select value={form.owner} onValueChange={setField('owner')}>
              <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED_OWNER}>Unassigned</SelectItem>
                {safeList(meta.owners).map((owner) => (
                  <SelectItem key={owner} value={owner}>{owner}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="1st Contact Date">
            <Input type="date" value={form.firstContactDate} onChange={setText('firstContactDate')} className={inputClass} />
          </Field>
          <Field label="Last Contact Date">
            <Input type="date" value={form.lastContactDate} onChange={setText('lastContactDate')} className={inputClass} />
          </Field>
          <Field label="Next Follow Up Date">
            <Input type="date" value={form.followupDate} onChange={setText('followupDate')} className={inputClass} />
          </Field>
          <Field label="Created by">
            <Input value={form.createdBy} onChange={setText('createdBy')} className={inputClass} />
          </Field>
        </div>
      </Section>

      <Section title="4. Agreement and Commission">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Contract Starting Date">
            <Input type="date" value={form.contractStartDate} onChange={setText('contractStartDate')} className={inputClass} />
          </Field>
          <Field label="Contract Renewal Date">
            <Input type="date" value={form.contractRenewalDate} onChange={setText('contractRenewalDate')} className={inputClass} />
          </Field>
          <Field label="Contract Ending Date">
            <Input type="date" value={form.contractEndDate} onChange={setText('contractEndDate')} className={inputClass} />
          </Field>
          <Field label="Internal Remarks" className="sm:col-span-2">
            <Textarea value={form.internalRemarks} onChange={setText('internalRemarks')} className={inputClass} rows={3} />
          </Field>
          {/* Tuition years and short-term programs are stored separately but
              belong to one rate card, so they are edited as one table. */}
          <div className="space-y-3 sm:col-span-2">
            <div className="flex items-center justify-between gap-3">
              <Label>Commission</Label>
              <Button type="button" variant="outline" size="sm" onClick={addCommissionRow} className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                <Plus className="mr-2 h-4 w-4" />
                Add year
              </Button>
            </div>
            <div className="overflow-hidden rounded-lg border border-[rgba(6,43,26,0.12)]">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 bg-[#F7F8FA] px-3 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/50">
                <span>Scope</span>
                <span>Percentage (%)</span>
                <span className="w-[88px]" />
              </div>
              {safeList(form.commissionSchedule).map((row, index) => (
                <div key={index} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 border-t border-[rgba(6,43,26,0.08)] px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#062B1A]/60">Year</span>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      aria-label={`Commission year for row ${index + 1}`}
                      value={row.year}
                      onChange={setCommissionRow(index, 'year')}
                      className={`${inputClass} max-w-[90px]`}
                    />
                  </div>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    aria-label={`Commission percentage for row ${index + 1}`}
                    value={row.percent}
                    onChange={setCommissionRow(index, 'percent')}
                    className={inputClass}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeCommissionRow(index)}
                    className="w-[88px] border-[rgba(220,38,38,0.28)] text-[rgb(153,27,27)] hover:bg-[rgba(220,38,38,0.06)]"
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 border-t border-[rgba(6,43,26,0.08)] bg-[#F7F8FA]/60 px-3 py-2">
                <span className="text-sm font-medium text-[#062B1A]">Short-term programs</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  aria-label="Short-term programs commission percentage"
                  value={form.shortProgramCommissionPercent}
                  onChange={setText('shortProgramCommissionPercent')}
                  className={inputClass}
                />
                <span className="w-[88px]" />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {customFields.length > 0 && (
        <Section title="Additional Information">
          <div className="grid gap-4 sm:grid-cols-2">
            {customFields.map((definition) => (
              <Field key={definition.id} label={definition.label}>
                <CustomFieldInput
                  definition={definition}
                  value={customValues[customFieldKey(definition.id)]}
                  onChange={(value) => setCustomField(definition, value)}
                  inputClass={inputClass}
                />
              </Field>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
